import React, { useMemo, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  Tab,
  Tabs,
  Input,
  Switch,
  Select,
  Button,
  Popover,
  Checkbox,
  Typography,
  SettingIcon,
  NavigateNextIcon
} from '@attentive-platform/stem-ui';

import { useRosettaStore } from '../../../Stores/useRosettaStore';
import { ScopeSelectionIcon, QuotePricingIcon, ResetIcon } from '../rosettaSvgs';
import { ROSETTA_TAB_ENUM, PAYMENT_TERMS_ENUM, PAYMENT_TERMS_LABELS } from '../../../Constants/Constants';

const SettingsRow = ({ label, children }) => (
  <div className='flex items-center justify-between gap-4 p-3'>
    <Typography className='mr-4'>{label}</Typography>
    {children}
  </div>
);

const SettingsDropdown = () => {
  const {
    showPricing,
    scopeMode,
    inflationPercentage,
    volumeDiscountTiers,
    showTiersTable,
    hiddenTiers,
    showPricingReference,
    paymentTerms,
    dispatch
  } = useRosettaStore(
    useShallow(state => ({
      showPricing: state.showPricing,
      scopeMode: state.scopeMode,
      inflationPercentage: state.inflationPercentage,
      volumeDiscountTiers: state.volumeDiscountTiers,
      showTiersTable: state.showTiersTable,
      hiddenTiers: state.hiddenTiers,
      showPricingReference: state.showPricingReference,
      paymentTerms: state.paymentTerms,
      dispatch: state.dispatch
    }))
  );

  const handleScopeModeToggle = useCallback(
    event => {
      dispatch({ type: 'SET_SCOPE_MODE', payload: event.target.checked });
      if (event.target.checked && showPricing) {
        dispatch({ type: 'SET_SHOW_PRICING', payload: false });
      }
    },
    [dispatch, showPricing]
  );

  const handleInflationChange = useCallback(
    event => {
      const value = event.target.value;
      if (value === '') {
        dispatch({ type: 'SET_INFLATION_PERCENTAGE', payload: 0 });
        return;
      }
      const inflationValue = parseFloat(value);
      if (!isNaN(inflationValue) && inflationValue >= 0) {
        dispatch({ type: 'SET_INFLATION_PERCENTAGE', payload: inflationValue });
      }
    },
    [dispatch]
  );

  const handleDispatch = useCallback(
    (type, payload) => {
      dispatch({ type, payload });
    },
    [dispatch]
  );

  const tiersList = useMemo(() => Object.keys(volumeDiscountTiers), [volumeDiscountTiers]);

  const tiersVisibilityContent = useMemo(
    () => (
      <div className='min-w-[140px] p-3'>
        <div className='font-semibold text-gray-500'>Visibility Settings</div>
        <div className='flex items-center gap-2 mb-2'>
          <Checkbox
            size='small'
            label='Show Table'
            checked={showTiersTable}
            onChange={e => handleDispatch('SET_SHOW_TIERS_TABLE', e.target.checked)}
          />
        </div>

        <div className='font-semibold text-gray-500'>Hide Specific Tiers</div>

        {tiersList.map(sheetCount => {
          const tierKey = parseInt(sheetCount, 10);
          return (
            <div key={sheetCount}>
              <Checkbox
                size='small'
                checked={!hiddenTiers.has(tierKey)}
                label={`${tierKey.toLocaleString()}+ Sheets`}
                onChange={() => handleDispatch('TOGGLE_TIER_VISIBILITY', tierKey)}
              />
            </div>
          );
        })}
      </div>
    ),
    [tiersList, showTiersTable, hiddenTiers, handleDispatch]
  );

  const paymentTermsOptions = useMemo(
    () => [
      { value: PAYMENT_TERMS_ENUM.QUARTERLY, label: PAYMENT_TERMS_LABELS[PAYMENT_TERMS_ENUM.QUARTERLY] },
      { value: PAYMENT_TERMS_ENUM.SEMI_ANNUAL, label: PAYMENT_TERMS_LABELS[PAYMENT_TERMS_ENUM.SEMI_ANNUAL] },
      { value: PAYMENT_TERMS_ENUM.HIDDEN, label: PAYMENT_TERMS_LABELS[PAYMENT_TERMS_ENUM.HIDDEN] }
    ],
    []
  );

  const dropdownContent = (
    <div className='max-w-[280px]'>
      <SettingsRow label='Scope Mode'>
        <Switch size='small' checked={scopeMode} onChange={handleScopeModeToggle} />
      </SettingsRow>

      <SettingsRow label='Payment Terms'>
        <Select
          size='small'
          value={paymentTerms}
          variant='std-filled'
          items={paymentTermsOptions}
          className='payment-terms-select'
          onChange={value => handleDispatch('SET_PAYMENT_TERMS', value)}
        />
      </SettingsRow>

      <SettingsRow label='Pricing Reference'>
        <Switch
          size='small'
          checked={showPricingReference}
          onChange={e => handleDispatch('SET_SHOW_PRICING_REFERENCE', e.target.checked)}
        />
      </SettingsRow>

      <Popover
        triggerEvent='hover'
        content={tiersVisibilityContent}
        popoverProps={{ placement: 'bottom-end' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}>
        <div className='flex items-center gap-1 p-3 cursor-pointer hover:bg-gray-50'>
          <Typography>Tiers Visibility</Typography>
          <NavigateNextIcon />
        </div>
      </Popover>

      <div className='flex items-center justify-between gap-4 p-3'>
        <Typography>Calibration</Typography>
        <Input
          size='small'
          type='number'
          className='w-[50px]'
          value={inflationPercentage}
          onChange={handleInflationChange}
        />
      </div>
    </div>
  );

  return (
    <Popover
      triggerEvent='click'
      content={dropdownContent}
      popoverProps={{ placement: 'bottom-end' }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}>
      <SettingIcon fill='#4461ed' className='cursor-pointer ml-4' />
    </Popover>
  );
};

const RosettaHeader = ({ onReset }) => {
  const { showPricing, scopeMode, dispatch } = useRosettaStore(
    useShallow(state => ({
      showPricing: state.showPricing,
      scopeMode: state.scopeMode,
      dispatch: state.dispatch
    }))
  );

  const handleTabChange = useCallback(
    (_, newValue) => {
      dispatch({ type: 'SET_SHOW_PRICING', payload: newValue === ROSETTA_TAB_ENUM.QUOTE_PRICING });
    },
    [dispatch]
  );

  return (
    <header className='rosetta-header'>
      <div className='rosetta-header-content'>
        <div className='rosetta-logo'>
          <h1>Rosetta: Beam AI</h1>
          <span className='rosetta-subtitle'>Scope & Pricing Configurator</span>
        </div>
        <div className='rosetta-tab-navigation'>
          <Tabs
            value={showPricing ? ROSETTA_TAB_ENUM.QUOTE_PRICING : ROSETTA_TAB_ENUM.SCOPE_SELECTION}
            onChange={handleTabChange}
            textColor='primary'
            indicatorColor='primary'>
            <Tab
              value={ROSETTA_TAB_ENUM.SCOPE_SELECTION}
              label={
                <div className='flex items-center gap-1'>
                  <ScopeSelectionIcon />
                  <span>Scope Selection</span>
                </div>
              }
            />
            {!scopeMode && (
              <Tab
                className='ml-2'
                value={ROSETTA_TAB_ENUM.QUOTE_PRICING}
                label={
                  <div className='flex items-center gap-1'>
                    <QuotePricingIcon />
                    <span>Quote & Pricing</span>
                  </div>
                }
              />
            )}
          </Tabs>
        </div>
        <div>
          <Button variant='outlined' onClick={onReset}>
            <ResetIcon />
            Reset
          </Button>
          <SettingsDropdown />
        </div>
      </div>
    </header>
  );
};

export default RosettaHeader;
